import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HistoryIcon from "@mui/icons-material/History";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import FactoryIcon from "@mui/icons-material/Factory";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import AssessmentIcon from "@mui/icons-material/Assessment";

import { useUser } from "./UserContext";
import UserLogOut from "./UserLogOut";
import { useMachineSupervisor } from "./MachineSupervisorContext";
import MachineHourApproval from "./MachineHourApproval";
import MachineHourEditHistoryTab from "./MachineHourEditHistoryTab";
import ShiftLogsTab from "./admin/ShiftLogsTab";
import OperationLogsTab from "./admin/OperationLogsTab";
import JobSummaryTab from "./admin/JobSummaryTab";
import RecentShiftsTab from "./admin/RecentShiftsTab";
import MasterWorkstationsView from "./machineSupervisor/MasterWorkstationsView";
import MasterEmployeesView from "./machineSupervisor/MasterEmployeesView";
import MasterUsersView from "./machineSupervisor/MasterUsersView";
import MachineSupervisorPlanVsActualReport from "./machineSupervisor/MachineSupervisorPlanVsActualReport";
import MachineSupervisorLineupJobsView from "./machineSupervisor/MachineSupervisorLineupJobsView";

import { AdminThemeProvider, useAdminTheme } from "./admin/AdminThemeContext";
import { adminDarkTheme, adminLightTheme, T } from "./admin/adminTheme";
import { SidebarRoot, MainArea, NavItem, StatCard } from "./admin/shared";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const SIDEBAR_EXPANDED_WIDTH = 286;
const SIDEBAR_COLLAPSED_WIDTH = 82;
const SIDEBAR_COLLAPSED_STORAGE_KEY = "sc_machine_supervisor_sidebar_collapsed";

const TOUCH_TARGET = 48;

const touchRootSx = {
  WebkitTapHighlightColor: "transparent",
  touchAction: "manipulation",
  "@media (pointer: coarse)": {
    "& .MuiButton-root": {
      minHeight: TOUCH_TARGET,
      borderRadius: "12px",
      fontSize: 14,
    },
    "& .MuiIconButton-root": {
      minWidth: TOUCH_TARGET,
      minHeight: TOUCH_TARGET,
      borderRadius: "12px",
    },
    "& .MuiListItemButton-root": {
      minHeight: 56,
    },
    "& .MuiInputBase-root": {
      minHeight: TOUCH_TARGET,
      fontSize: 14,
    },
    "& .MuiInputLabel-root": {
      fontSize: 14,
    },
    "& .MuiMenuItem-root": {
      minHeight: TOUCH_TARGET,
      fontSize: 14,
    },
    "& .MuiTableCell-root": {
      py: 1.45,
      fontSize: 14,
    },
    "& .MuiChip-root": {
      minHeight: 32,
      borderRadius: "10px",
      fontSize: 12.5,
    },
  },
};

const NAV_GROUPS = [
  {
    label: "Dashboard",
    items: [
      {
        key: "overview",
        label: "Overview",
        icon: <DashboardIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },


  {
    label: "Supervisor Review",
    items: [
      {
        key: "review",
        label: "Review/Edit Logs",
        icon: <FactCheckIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "history",
        label: "Edit History",
        icon: <HistoryIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "planvsactual",
        label: "Plan Vs Actual",
        icon: <AssessmentIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
  {
    label: "Master Data",
    items: [
      {
        key: "masterWorkstations",
        label: "Workstations",
        icon: <PrecisionManufacturingIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "masterEmployees",
        label: "Employees",
        icon: <PersonAddIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "masterUsers",
        label: "Users",
        icon: <ManageAccountsIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
  {
    label: "Logs & Records",
    items: [
            {
              key: "lineupjobs",
              label: "Lineup Jobs",
              icon: <ListAltIcon sx={{ fontSize: 18 }} />,
            },
      {
        key: "shiftlogs",
        label: "Shift Logs",
        icon: <HistoryIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "operationlogs",
        label: "Operation Logs",
        icon: <ListAltIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "jobsummary",
        label: "Job Summary",
        icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: "recentshifts",
        label: "Recent Shifts",
        icon: <WatchLaterIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
];

const ALL_NAV = NAV_GROUPS.flatMap((group) => group.items);

function hasActiveShift(machine) {
  return (
    Number(machine?.HasActiveShift) === 1 ||
    String(machine?.DisplayStatus || "")
      .trim()
      .toLowerCase() === "logged-in" ||
    String(machine?.CurrentShiftStatus || "")
      .trim()
      .toLowerCase() === "logged-in"
  );
}

function getCrewText(machine) {
  const crewText = machine?.CurrentCrewText;

  if (crewText && String(crewText).trim()) {
    return crewText;
  }

  return [
    machine?.CurrentOperator,
    machine?.CurrentAssistantOperator,
    machine?.CurrentHelpers,
  ]
    .filter(Boolean)
    .join(", ");
}

function ThemeToggleButton({ collapsed = false }) {
  const { isDark, toggleMode, sidebarColors } = useAdminTheme();

  return (
    <Tooltip
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      placement="right"
      arrow
    >
      <Box
        onClick={toggleMode}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 1.7,
          py: { xs: 1.25, md: 1.1 },
          mx: 1.1,
          minHeight: collapsed ? 54 : 50,
          borderRadius: "13px",
          cursor: "pointer",
          border: `0.5px solid ${sidebarColors.border}`,
          background: "rgba(255,255,255,0.08)",
          color: sidebarColors.text,
          transition: "all 0.22s ease",
          "&:hover": {
            background: sidebarColors.hover,
            borderColor: sidebarColors.activeIcon,
            transform: collapsed ? "translateY(-1px)" : "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: collapsed ? 0 : 1.1,
          }}
        >
          {isDark ? (
            <LightModeIcon
              sx={{ fontSize: 16, color: sidebarColors.activeIcon }}
            />
          ) : (
            <DarkModeIcon
              sx={{ fontSize: 16, color: sidebarColors.activeIcon }}
            />
          )}

          {!collapsed && (
            <Typography
              sx={{
                fontSize: 13.2,
                fontWeight: 700,
                color: sidebarColors.text,
              }}
            >
              {isDark ? "Light mode" : "Dark mode"}
            </Typography>
          )}
        </Box>

        {!collapsed && (
          <Box
            sx={{
              width: 37,
              height: 20,
              borderRadius: "20px",
              background: "rgba(255,255,255,0.22)",
              border: `0.5px solid ${sidebarColors.border}`,
              position: "relative",
              transition: "background 0.25s ease",
              flexShrink: 0,
              boxShadow: "0 0 8px rgba(255,255,255,0.10)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 2.25,
                left: isDark ? 19 : 2.25,
                width: 15.5,
                height: 15.5,
                borderRadius: "50%",
                background: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function SectionDivider({ label, t }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 1.25,
        mt: 0.5,
      }}
    >
      <Box
        sx={{
          width: 3,
          height: 14,
          borderRadius: "3px",
          background: t.blue,
          opacity: 0.7,
        }}
      />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: t.muted,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "0.5px", background: t.border }} />
    </Box>
  );
}

function LoadingState({ t }) {
  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        <CircularProgress size={30} sx={{ color: t.blue }} />
        <Typography sx={{ fontSize: 13, color: t.muted, fontWeight: 600 }}>
          Loading supervisor dashboard...
        </Typography>
      </Stack>
    </Box>
  );
}

function MachineStatusChip({ machine }) {
  const isActive = hasActiveShift(machine);

  return (
    <Chip
      size="small"
      label={isActive ? "Active" : "Idle"}
      color={isActive ? "success" : "warning"}
      variant={isActive ? "filled" : "outlined"}
      sx={{ fontWeight: 700, minWidth: 70 }}
    />
  );
}

function OverviewContent({
  machines,
  machinesSupervisor,
  allocatedGroups,
  groupBreakdownRows,
  supervisorId,
  setActiveTab,
  onRefresh,
  loading,
  t,
  isDark,
}) {
  const activeMachineCount = useMemo(
    () => machines.filter((machine) => hasActiveShift(machine)).length,
    [machines],
  );

  const idleMachineCount = Math.max(machines.length - activeMachineCount, 0);

  const quickAccessItems = [
    {
      key: "review",
      label: "Review/Edit Logs",
      desc: "Approve and edit machine-hour summaries",
      icon: <FactCheckIcon sx={{ fontSize: 18 }} />,
      color: t.blue,
    },
    {
      key: "history",
      label: "Edit History",
      desc: "Trace edited job summary records",
      icon: <HistoryIcon sx={{ fontSize: 18 }} />,
      color: t.violet,
    },
    {
      key: "planvsactual",
      label: "Plan Vs Actual",
      desc: "Review planned vs actual output",
      icon: <AssessmentIcon sx={{ fontSize: 18 }} />,
      color: t.green,
    },
        {
          key: "lineupjobs",
          label: "Lineup Jobs",
          desc: "View planned lineup jobs for allocated machines",
          icon: <ListAltIcon sx={{ fontSize: 18 }} />,
          color: "#6366f1",
        },
    {
      key: "masterWorkstations",
      label: "Workstations",
      desc: "View allocated workstation master data",
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 18 }} />,
      color: "#0ea5e9",
    },
    {
      key: "masterEmployees",
      label: "Employees",
      desc: "View employees within allocated groups",
      icon: <PersonAddIcon sx={{ fontSize: 18 }} />,
      color: "#22c55e",
    },
    {
      key: "masterUsers",
      label: "Users",
      desc: "View users within allocated groups",
      icon: <ManageAccountsIcon sx={{ fontSize: 18 }} />,
      color: "#8b5cf6",
    },
    {
      key: "shiftlogs",
      label: "Shift Logs",
      desc: "Search allocated workstation shift logs",
      icon: <HistoryIcon sx={{ fontSize: 18 }} />,
      color: t.amber,
    },
    {
      key: "operationlogs",
      label: "Operation Logs",
      desc: "Review allocated workstation operations",
      icon: <ListAltIcon sx={{ fontSize: 18 }} />,
      color: "#06b6d4",
    },
    {
      key: "jobsummary",
      label: "Job Summary",
      desc: "Check production and idle summaries",
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />,
      color: "#f97316",
    },
    {
      key: "recentshifts",
      label: "Recent Shifts",
      desc: "Monitor latest operator shift activity",
      icon: <WatchLaterIcon sx={{ fontSize: 18 }} />,
      color: "#14b8a6",
    },
  ];

  return (
    <Fade in>
      <Box>
        <Paper
          sx={{
            borderRadius: "16px",
            p: { xs: 1.5, md: 2 },
            mb: 2.5,
            background: t.s1,
            border: `0.5px solid ${t.border}`,
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 1.25,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 14,
                  color: t.heading,
                  letterSpacing: "-0.01em",
                }}
              >
                Overview quick access
              </Typography>
              <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>
                Open the updated supervisor tabs directly from this overview.
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${quickAccessItems.length} shortcuts`}
              variant="outlined"
              sx={{ fontWeight: 800, color: t.muted, borderColor: t.border }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {quickAccessItems.map((item) => (
              <Box
                key={item.key}
                component="button"
                type="button"
                onClick={() => setActiveTab(item.key)}
                sx={{
                  width: "100%",
                  minHeight: { xs: 92, md: 82 },
                  p: { xs: 1.45, md: 1.25 },
                  borderRadius: "13px",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  background: t.s2,
                  border: `0.5px solid ${t.border}`,
                  color: "inherit",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.15,
                  transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": {
                    background: t.s3,
                    borderColor: item.color,
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${item.color}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    background: `${item.color}18`,
                    border: `0.5px solid ${item.color}28`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: item.color,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: t.heading,
                      lineHeight: 1.25,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10.8,
                      color: t.muted,
                      mt: 0.35,
                      fontWeight: 500,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Allocated machines"
              value={machinesSupervisor?.machineCount ?? machines.length ?? 0}
              color={t.blue}
              icon={<PrecisionManufacturingIcon sx={{ fontSize: 20 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Active machines"
              value={machinesSupervisor?.activeMachines ?? activeMachineCount}
              color={t.green}
              icon={<PlayCircleOutlineIcon sx={{ fontSize: 20 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Idle machines"
              value={machinesSupervisor?.idleMachines ?? idleMachineCount}
              color={t.amber}
              icon={<PauseCircleOutlineIcon sx={{ fontSize: 20 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Machine groups"
              value={allocatedGroups.length}
              color={t.violet}
              icon={<FactoryIcon sx={{ fontSize: 20 }} />}
            />
          </Grid>
        </Grid>

        <Paper
          sx={{
            borderRadius: "14px",
            p: 2.5,
            mb: 2.5,
            background: t.s1,
            border: `0.5px solid ${t.border}`,
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: t.heading,
                  letterSpacing: "-0.01em",
                }}
              >
                Allocated machine groups
              </Typography>
              <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>
                Supervisor ID: {supervisorId}
              </Typography>
            </Box>

            <Tooltip
              title="Refresh allocated machines and active shift data"
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  disabled={loading}
                  sx={{
                    border: `0.5px solid ${t.border}`,
                    borderRadius: "9px",
                    width: 34,
                    height: 34,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16, color: t.muted }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            {allocatedGroups.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: t.muted }}>
                No machine groups allocated.
              </Typography>
            ) : (
              allocatedGroups.map((groupCode) => (
                <Chip
                  key={groupCode}
                  size="small"
                  label={groupCode}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              ))
            )}
          </Stack>

          <Grid container spacing={1.5}>
            {groupBreakdownRows.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">No group breakdown available.</Alert>
              </Grid>
            ) : (
              groupBreakdownRows.map((row) => (
                <Grid item xs={12} sm={6} md={4} key={row.groupCode}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "11px",
                      background: t.s2,
                      border: `0.5px solid ${t.border}`,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 800, color: t.heading }}
                    >
                      {row.groupCode}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.3 }}>
                      Total: {row.total} | Active: {row.active} | Idle:{" "}
                      {row.idle}
                    </Typography>
                  </Box>
                </Grid>
              ))
            )}
          </Grid>
        </Paper>

        <SectionDivider label="Allocated workstations" t={t} />

        <Paper
          sx={{
            borderRadius: "14px",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            background: t.s1,
            border: `0.5px solid ${t.border}`,
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
          }}
        >
          <Table size="small" sx={{ minWidth: 1150 }}>
            <TableHead>
              <TableRow sx={{ background: t.s2 }}>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Machine Code
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Group
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Current Operator
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Current Crew
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                  Shift Start
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {machines.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, color: t.muted }}
                  >
                    No machine-hour workstations are allocated to this
                    supervisor.
                  </TableCell>
                </TableRow>
              ) : (
                machines.map((machine) => {
                  const crewText = getCrewText(machine);
                  const operatorValue =
                    machine.CurrentOperator ||
                    machine.CurrentEmployeeID ||
                    machine.activeUserEmpId ||
                    "-";

                  return (
                    <TableRow key={machine.MachineCode} hover>
                      <TableCell sx={{ fontWeight: 800, color: t.heading }}>
                        {machine.MachineCode}
                      </TableCell>
                      <TableCell sx={{ color: t.primary }}>
                        {machine.MachineDescription}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={machine.MachineGroupCode || "-"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <MachineStatusChip machine={machine} />
                      </TableCell>
                      <TableCell sx={{ color: t.primary, minWidth: 150 }}>
                        <Stack spacing={0.35}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 800,
                              color: t.heading,
                            }}
                          >
                            {operatorValue}
                          </Typography>

                          {machine.CurrentEmployeeID && (
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                color: t.muted,
                                fontFamily: "monospace",
                              }}
                            >
                              Emp ID: {machine.CurrentEmployeeID}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: t.primary, minWidth: 260 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: crewText ? t.primary : t.muted,
                            lineHeight: 1.45,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {crewText || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ color: t.primary, whiteSpace: "nowrap" }}
                      >
                        {machine.CurrentShiftStart || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Fade>
  );
}

function MachineSupervisorDashboardInner() {
  const userContext = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { sidebarColors } = useAdminTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const {
    fetchAllocatedMachines,
    machines,
    loading,
    error,
    machinesSupervisor,
  } = useMachineSupervisor();

  const [activeTab, setActiveTab] = useState("overview");
  const [pageError, setPageError] = useState("");
  const [supervisorId, setSupervisorId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [overviewMachines, setOverviewMachines] = useState([]);
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;

  const activeGroupLabel = useMemo(
    () =>
      NAV_GROUPS.find((group) =>
        group.items.some((item) => item.key === activeTab),
      )?.label || "",
    [activeTab],
  );

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    NAV_GROUPS.forEach((group) => {
      initial[group.label] = group.items.some(
        (item) => item.key === "overview",
      );
    });
    return initial;
  });

  useEffect(() => {
    if (activeGroupLabel) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroupLabel]: true }));
    }
  }, [activeGroupLabel]);

  useEffect(() => {
    const id =
      userContext?.username ||
      location.state?.username ||
      sessionStorage.getItem("username") ||
      localStorage.getItem("username");

    if (!id) {
      setPageError("No supervisor ID found. Redirecting to login...");
      const timer = setTimeout(() => navigate("/"), 2000);
      return () => clearTimeout(timer);
    }

    setSupervisorId(id);
    return undefined;
  }, [userContext?.username, location.state, navigate]);

  useEffect(() => {
    if (supervisorId) {
      fetchAllocatedMachines(supervisorId);
    }
  }, [supervisorId, fetchAllocatedMachines]);

  const onSnack = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchOverviewMachines = useCallback(async () => {
    if (!supervisorId) return false;

    setOverviewLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/overview-machines/${supervisorId}`,
      );

      const data = res.data || {};
      const machineList = Array.isArray(data)
        ? data
        : Array.isArray(data.machines)
          ? data.machines
          : [];

      setOverviewMachines(machineList);

      setOverviewSummary(
        data.summary || {
          machineCount: machineList.length,
          activeMachines: machineList.filter((machine) =>
            hasActiveShift(machine),
          ).length,
          idleMachines: machineList.filter(
            (machine) => !hasActiveShift(machine),
          ).length,
        },
      );

      return true;
    } catch (err) {
      setOverviewMachines([]);
      setOverviewSummary(null);

      onSnack(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load overview active shift data.",
        "error",
      );

      return false;
    } finally {
      setOverviewLoading(false);
    }
  }, [onSnack, supervisorId]);

  useEffect(() => {
    if (supervisorId && activeTab === "overview") {
      fetchOverviewMachines();
    }
  }, [activeTab, fetchOverviewMachines, supervisorId]);

  const overviewMachineList = useMemo(() => {
    return overviewMachines.length > 0 ? overviewMachines : machines;
  }, [machines, overviewMachines]);

  const allocatedGroups = useMemo(() => {
    const groups = new Set();
    machines.forEach((machine) => {
      if (machine.MachineGroupCode) groups.add(machine.MachineGroupCode);
    });
    return Array.from(groups).sort();
  }, [machines]);

  const groupBreakdownRows = useMemo(() => {
    const summary = {};

    overviewMachineList.forEach((machine) => {
      const groupCode = machine.MachineGroupCode || "OTHER";
      if (!summary[groupCode]) {
        summary[groupCode] = { groupCode, total: 0, active: 0, idle: 0 };
      }

      summary[groupCode].total += 1;

      if (hasActiveShift(machine)) {
        summary[groupCode].active += 1;
      } else {
        summary[groupCode].idle += 1;
      }
    });

    return Object.values(summary).sort((a, b) =>
      a.groupCode.localeCompare(b.groupCode),
    );
  }, [overviewMachineList]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch (_) {}
      return next;
    });
  }, []);

  const toggleGroup = useCallback((label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const refreshAllocatedMachines = useCallback(async () => {
    if (!supervisorId) return;

    const ok = await fetchAllocatedMachines(supervisorId);

    if (activeTab === "overview") {
      await fetchOverviewMachines();
    }

    onSnack(
      ok
        ? "Allocated machines refreshed"
        : "Failed to refresh allocated machines",
      ok ? "success" : "error",
    );
  }, [
    activeTab,
    fetchAllocatedMachines,
    fetchOverviewMachines,
    onSnack,
    supervisorId,
  ]);

  const activeLabel = ALL_NAV.find((nav) => nav.key === activeTab)?.label || "";

  const renderNavItem = useCallback(
    (item) => {
      const isActive = activeTab === item.key;

      return (
        <Tooltip
          key={item.key}
          title={sidebarCollapsed ? item.label : ""}
          placement="right"
          arrow
          disableHoverListener={!sidebarCollapsed}
        >
          <ListItemButton
            disableRipple
            onClick={() => setActiveTab(item.key)}
            sx={{ p: 0, "&:hover": { background: "transparent" } }}
          >
            <NavItem
              active={isActive ? 1 : 0}
              sx={{
                flex: 1,
                minHeight: sidebarCollapsed ? 58 : 56,
                mx: sidebarCollapsed ? 1 : 1.1,
                px: sidebarCollapsed ? 0.65 : 1.15,
                py: sidebarCollapsed ? 0.55 : 0.65,
                borderRadius: "11px",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
                position: "relative",
                background: isActive ? sidebarColors.active : "transparent",
                borderColor: isActive ? sidebarColors.border : "transparent",
                boxShadow: isActive
                  ? "inset 0 1px 0 rgba(255,255,255,0.08)"
                  : "none",
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                "&:hover": {
                  background: isActive
                    ? sidebarColors.activeStrong
                    : sidebarColors.hover,
                  transform: sidebarCollapsed
                    ? "translateX(1px)"
                    : "translateX(2px)",
                },
                "& .MuiListItemText-root": { m: 0 },
              }}
            >
              <Box
                sx={{
                  width: sidebarCollapsed ? 42 : 38,
                  height: sidebarCollapsed ? 42 : 38,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: isActive
                    ? "rgba(255,255,255,0.16)"
                    : "transparent",
                  color: isActive
                    ? sidebarColors.activeIcon
                    : sidebarColors.icon,
                  transition: "all 0.18s ease",
                }}
              >
                {item.icon}
              </Box>

              {!sidebarCollapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 15.2,
                    fontWeight: isActive ? 800 : 650,
                    color: isActive
                      ? sidebarColors.activeText
                      : sidebarColors.text,
                    letterSpacing: isActive ? "-0.01em" : "normal",
                  }}
                />
              )}

              {isActive && (
                <Box
                  sx={
                    sidebarCollapsed
                      ? {
                          position: "absolute",
                          right: 7,
                          width: 5.5,
                          height: 5.5,
                          borderRadius: "50%",
                          background: sidebarColors.activeIcon,
                          boxShadow: `0 0 6px ${sidebarColors.activeIcon}`,
                        }
                      : {
                          width: 5.5,
                          height: 5.5,
                          borderRadius: "50%",
                          background: sidebarColors.activeIcon,
                          flexShrink: 0,
                          boxShadow: `0 0 6px ${sidebarColors.activeIcon}`,
                        }
                  }
                />
              )}
            </NavItem>
          </ListItemButton>
        </Tooltip>
      );
    },
    [activeTab, sidebarCollapsed, sidebarColors],
  );

  const renderMainContent = () => {
    if (pageError) {
      return <Alert severity="error">{pageError}</Alert>;
    }

    if (!supervisorId || loading) {
      return <LoadingState t={t} />;
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (activeTab !== "overview" && machines.length === 0) {
      return (
        <Alert severity="info">
          No machine-hour workstations are allocated to this supervisor.
        </Alert>
      );
    }

    if (activeTab === "overview") {
      return (
        <OverviewContent
          machines={overviewMachineList}
          machinesSupervisor={overviewSummary || machinesSupervisor}
          allocatedGroups={allocatedGroups}
          groupBreakdownRows={groupBreakdownRows}
          supervisorId={supervisorId}
          setActiveTab={setActiveTab}
          onRefresh={refreshAllocatedMachines}
          loading={loading || overviewLoading}
          t={t}
          isDark={isDark}
        />
      );
    }

    if (activeTab === "review") {
      return (
        <MachineHourApproval
          username={supervisorId}
          allocatedMachines={machines}
          restrictedMode
          title="Machine-Hour Supervisor Review"
          subtitle="Review and edit only your allocated workstations"
          updateUrl={`${API_BASE_URL}/api/machinesupervisor/job-summary/update-with-log`}
          editSource="MACHINE_SUPERVISOR_REVIEW"
          jobReviewUrls={{
            logs: `${API_BASE_URL}/api/machinesupervisor/job-review-logs/${supervisorId}`,
            summary: `${API_BASE_URL}/api/machinesupervisor/job-review-summary/${supervisorId}`,
            plan: `${API_BASE_URL}/api/machinesupervisor/job-plan/${supervisorId}`,
          }}
        />
      );
    }

    if (activeTab === "history") {
      return (
        <MachineHourEditHistoryTab
          supervisorId={supervisorId}
          allocatedMachines={machines}
        />
      );
    }

    if (activeTab === "planvsactual") {
      return (
        <MachineSupervisorPlanVsActualReport
          supervisorId={supervisorId}
          allocatedMachines={machines}
          allocatedGroups={allocatedGroups}
          onSnack={onSnack}
        />
      );
    }


    if (activeTab === "masterWorkstations") {
      return (
        <MasterWorkstationsView supervisorId={supervisorId} onSnack={onSnack} />
      );
    }

    if (activeTab === "masterEmployees") {
      return (
        <MasterEmployeesView supervisorId={supervisorId} onSnack={onSnack} />
      );
    }

    if (activeTab === "masterUsers") {
      return <MasterUsersView supervisorId={supervisorId} onSnack={onSnack} />;
    }
    
        if (activeTab === "lineupjobs") {
          return (
            <MachineSupervisorLineupJobsView
              supervisorId={supervisorId}
              allocatedMachines={machines}
              onSnack={onSnack}
              t={t}
              isDark={isDark}
            />
          );
        }
    if (activeTab === "shiftlogs") {
      return (
        <ShiftLogsTab
          onSnack={onSnack}
          endpoint={`${API_BASE_URL}/api/machinesupervisor/shiftlogs/search/${supervisorId}`}
          allocatedMachines={machines}
        />
      );
    }

    if (activeTab === "operationlogs") {
      return (
        <OperationLogsTab
          onSnack={onSnack}
          endpoint={`${API_BASE_URL}/api/machinesupervisor/operationlogs/search/${supervisorId}`}
          allocatedMachines={machines}
        />
      );
    }

    if (activeTab === "jobsummary") {
      return (
        <JobSummaryTab
          onSnack={onSnack}
          endpoint={`${API_BASE_URL}/api/machinesupervisor/jobsummary/search/${supervisorId}`}
          allocatedMachines={machines}
        />
      );
    }

    if (activeTab === "recentshifts") {
      return (
        <RecentShiftsTab
          onSnack={onSnack}
          endpoint={`${API_BASE_URL}/api/machinesupervisor/shiftlogs/search/${supervisorId}`}
          jobSummaryByShiftEndpoint={`${API_BASE_URL}/api/machinesupervisor/jobsummary/by-shift/${supervisorId}`}
          operationLogByShiftEndpoint={`${API_BASE_URL}/api/machinesupervisor/operationlog/by-shift/${supervisorId}`}
          allocatedMachines={machines}
        />
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: t.appBg,
        transition: "background 0.25s ease",
        ...touchRootSx,
      }}
    >
      <SidebarRoot
        sx={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          maxWidth: sidebarWidth,
          flexBasis: sidebarWidth,
          background: sidebarColors.background,
          borderRight: `1px solid ${sidebarColors.border}`,
          transition:
            "width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            px: sidebarCollapsed ? 0.9 : 2.2,
            py: 2.2,
            borderBottom: `1px solid ${sidebarColors.divider}`,
            display: "flex",
            flexDirection: sidebarCollapsed ? "column" : "row",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            gap: sidebarCollapsed ? 1 : 1.65,
            flexShrink: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 60%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: sidebarCollapsed ? 0 : 1.65,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                background: "linear-gradient(135deg, #ffffff 0%, #a0d8ff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow:
                  "0 2px 8px rgba(160,216,255,0.45), 0 0 0 1px rgba(160,216,255,0.25)",
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 20, color: "#0078d4" }} />
            </Box>

            {!sidebarCollapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14.5,
                    color: sidebarColors.text,
                    lineHeight: 1.2,
                    letterSpacing: "-0.02em",
                    whiteSpace: "nowrap",
                  }}
                >
                  Machine Supervisor
                </Typography>
                <Typography
                  sx={{
                    color: sidebarColors.textSecondary,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    mt: 0.25,
                  }}
                >
                  MACHINE HOURS
                </Typography>
              </Box>
            )}
          </Box>

          <Tooltip
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            placement="right"
            arrow
          >
            <IconButton
              size="small"
              onClick={toggleSidebarCollapsed}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                color: sidebarColors.text,
                border: `0.5px solid ${sidebarColors.border}`,
                background: "rgba(255,255,255,0.08)",
                flexShrink: 0,
                "&:hover": {
                  background: sidebarColors.hover,
                  color: sidebarColors.activeIcon,
                },
              }}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              ) : (
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              pt: sidebarCollapsed ? 0.9 : 0.7,
              pb: 1.1,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.18) transparent",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 4,
                background: "rgba(255,255,255,0.18)",
                "&:hover": { background: "rgba(255,255,255,0.30)" },
              },
            }}
          >
            {NAV_GROUPS.map((group) => {
              const isOpen = !!expandedGroups[group.label];
              const groupActive = group.items.some(
                (item) => item.key === activeTab,
              );
              const groupItemCount = group.items.length;
              return (
                <Box
                  key={group.label}
                  sx={{ mb: sidebarCollapsed ? 0.25 : 0.4 }}
                >
                  {sidebarCollapsed && group.label !== "Dashboard" && (
                    <Box
                      sx={{
                        mx: 2.4,
                        my: 0.45,
                        height: "0.5px",
                        background: sidebarColors.divider,
                        opacity: 0.45,
                      }}
                    />
                  )}

                  {!sidebarCollapsed && (
                    <Box
                      onClick={() => toggleGroup(group.label)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mx: 1.1,
                        mt: 1.1,
                        mb: 0.35,
                        px: 1.4,
                        py: 0.75,
                        borderRadius: "9px",
                        cursor: "pointer",
                        transition: "background 0.14s ease",
                        userSelect: "none",
                        background:
                          !isOpen && groupActive
                            ? sidebarColors.active
                            : "transparent",
                        "&:hover": { background: sidebarColors.hover },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.1 }}
                      >
                        <Box
                          sx={{
                            width: 4.5,
                            height: 4.5,
                            borderRadius: "50%",
                            flexShrink: 0,
                            background: groupActive
                              ? sidebarColors.activeIcon
                              : sidebarColors.textSecondary,
                            opacity: groupActive ? 1 : 0.4,
                            boxShadow: groupActive
                              ? `0 0 5px ${sidebarColors.activeIcon}`
                              : "none",
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.10em",
                            textTransform: "uppercase",
                            color: groupActive
                              ? sidebarColors.activeIcon
                              : sidebarColors.textSecondary,
                          }}
                        >
                          {group.label}
                        </Typography>

                        {!isOpen && (
                          <Box
                            sx={{
                              px: 0.85,
                              py: 0.05,
                              borderRadius: "11px",
                              background: "rgba(255, 255, 255, 0.1)",
                              border: `0.5px solid ${sidebarColors.border}`,
                              lineHeight: "16px",
                              minWidth: 20,
                              textAlign: "center",
                              transition: "opacity 0.15s ease",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: sidebarColors.textSecondary,
                                lineHeight: "18px",
                              }}
                            >
                              {groupItemCount}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: 16,
                          color: groupActive
                            ? sidebarColors.activeIcon
                            : sidebarColors.textSecondary,
                          transition:
                            "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
                          transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                        }}
                      />
                    </Box>
                  )}

                  {sidebarCollapsed ? (
                    <List sx={{ px: 0, pt: 0.05, pb: 0.2 }}>
                      {group.items.map(renderNavItem)}
                    </List>
                  ) : (
                    <Collapse in={isOpen} timeout={200} unmountOnExit>
                      <List sx={{ px: 0, pt: 0.1, pb: 0.35 }}>
                        {group.items.map(renderNavItem)}
                      </List>
                    </Collapse>
                  )}

                  {!sidebarCollapsed && isOpen && (
                    <Box
                      sx={{
                        mx: 2.2,
                        mt: 0.6,
                        height: "0.5px",
                        background: sidebarColors.divider,
                        opacity: 0.55,
                      }}
                    />
                  )}
                </Box>
              );
            })}

            <Box sx={{ height: 10 }} />
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 31,
              pointerEvents: "none",
              background:
                "linear-gradient(to bottom, transparent, rgba(0, 90, 158, 0.86))",
            }}
          />
        </Box>

        <Box sx={{ pb: 1.65, flexShrink: 0 }}>
          <Divider
            sx={{ mb: 1.4, opacity: 0.55, borderColor: sidebarColors.divider }}
          />

          <ThemeToggleButton collapsed={sidebarCollapsed} />

          <Box sx={{ px: sidebarCollapsed ? 1 : 1.65, mt: 1.1 }}>
            {sidebarCollapsed ? (
              <Tooltip
                title={`${supervisorId || "Supervisor"} - Logged in`}
                placement="right"
                arrow
              >
                <Box
                  sx={{
                    height: 46,
                    borderRadius: "11px",
                    background: "rgba(255,255,255,0.04)",
                    border: `0.5px solid ${sidebarColors.divider}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "default",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.15)",
                      border: `0.5px solid ${sidebarColors.activeIcon}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: sidebarColors.activeIcon,
                        lineHeight: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      {String(supervisorId || "S")
                        .slice(0, 2)
                        .toUpperCase()}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            ) : (
              <Box
                sx={{
                  borderRadius: "11px",
                  p: "11px 13px",
                  background: "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${sidebarColors.divider}`,
                  borderLeft: `2.5px solid ${sidebarColors.activeIcon}`,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 29,
                      height: 29,
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.15)",
                      border: `0.5px solid ${sidebarColors.activeIcon}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: sidebarColors.activeIcon,
                        lineHeight: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      {String(supervisorId || "S")
                        .slice(0, 2)
                        .toUpperCase()}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: sidebarColors.text,
                        fontWeight: 700,
                        fontSize: 13.2,
                        lineHeight: 1.2,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {supervisorId || "Supervisor"}
                    </Typography>
                    <Typography
                      sx={{
                        color: sidebarColors.textSecondary,
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                      }}
                    >
                      SUPERVISOR
                    </Typography>
                  </Box>
                </Box>

                <UserLogOut username={supervisorId} />
              </Box>
            )}
          </Box>
        </Box>
      </SidebarRoot>

      <MainArea
        sx={{
          marginLeft: `${sidebarWidth}px`,
          width: `calc(100% - ${sidebarWidth}px)`,
          maxWidth: `calc(100% - ${sidebarWidth}px)`,
          flex: 1,
          transition:
            "margin-left 0.25s ease, width 0.25s ease, max-width 0.25s ease",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        <Box sx={{ mb: 3.5 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: t.blue,
              mb: 0.5,
              opacity: 0.85,
            }}
          >
            {activeTab === "overview"
              ? "Dashboard"
              : NAV_GROUPS.find((group) =>
                  group.items.some((item) => item.key === activeTab),
                )?.label || ""}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: t.heading,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                {activeTab === "overview"
                  ? "Machine Hours Supervision"
                  : activeLabel}
              </Typography>

              <Box
                sx={{
                  mb: "3px",
                  height: 3,
                  width: 32,
                  borderRadius: "3px",
                  background: `linear-gradient(90deg, ${t.blue}, transparent)`,
                  opacity: 0.7,
                }}
              />
            </Box>

            <Tooltip title="Refresh allocated machines" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={refreshAllocatedMachines}
                  disabled={!supervisorId || loading || overviewLoading}
                  sx={{
                    border: `0.5px solid ${t.border}`,
                    borderRadius: "8px",
                    width: { xs: 48, md: 34 },
                    height: { xs: 48, md: 34 },
                    "&:hover": {
                      transform: "rotate(180deg)",
                      transition: "transform 0.4s ease",
                    },
                  }}
                >
                  {loading || overviewLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16, color: t.muted }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {renderMainContent()}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
            sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MainArea>
    </Box>
  );
}

function ThemedMachineSupervisorDashboard() {
  const { mode } = useAdminTheme();
  const muiTheme = mode === "dark" ? adminDarkTheme : adminLightTheme;

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <MachineSupervisorDashboardInner />
    </ThemeProvider>
  );
}

export default function MachineSupervisorPage() {
  return (
    <AdminThemeProvider>
      <ThemedMachineSupervisorDashboard />
    </AdminThemeProvider>
  );
}
